import { useAuthStore } from "~/common/stores/AuthStore";
import { storeToRefs } from "pinia";

export default defineNuxtPlugin((nuxtApp) => {
  const authStore = useAuthStore();
  const { authTokens } = storeToRefs(authStore);
  const config = useRuntimeConfig();

  // `import.meta.server` is what the rest of the codebase tests, and what
  // survives the Nuxt 3 deprecation of `process.server`.
  const isServer = import.meta.server;

  // Every reload starts here: the store is empty on a fresh render, so the
  // token has to come back out of the request's own cookie header or the first
  // server-rendered request goes out unauthenticated.
  if (isServer && !authTokens.value) {
    const cookieHeader = useRequestHeaders(["cookie"]).cookie || "";

    // Anchored to the cookie name. The previous pattern was /token=([^;]+)/,
    // which matches the tail of `auth_token=` by luck but would just as
    // happily match any other cookie ending in "token" that came first in the
    // header, and hand its value over as the bearer token.
    const tokenMatch = cookieHeader.match(/(?:^|;\s*)auth_token=([^;]+)/);

    if (tokenMatch && tokenMatch[1]) {
      const token = decodeURIComponent(tokenMatch[1]);
      authTokens.value = `Bearer ${token}`;
    }
  }

  const baseURL =
    (config.public.apiBaseUrl as string | undefined) ??
    "http://localhost:3000/api";

  const $customFetch = $fetch.create({
    baseURL,

    onRequest({ options }) {
      // Clone headers to avoid modifying the shared object
      options.headers = new Headers(options.headers);

      // Add auth token if available
      if (authTokens.value) {
        options.headers.set("Authorization", `${authTokens.value}`);
      }

      // Add server-side specific headers if needed
      if (isServer) {
        // Additional server headers could go here if needed
      }
    },

    onResponseError(error) {
      // Handle authentication errors
      if (error.response?.status === 401) {
        // Only log user out on client-side to prevent SSR issues
        if (import.meta.client) {
          console.warn("Authentication token invalid or expired, logging out");

          // Add a small delay to ensure we don't interrupt the current request flow
          setTimeout(() => {
            // Always drop the rejected token, even on public pages.
            authStore.resetAuthStore();

            // Only send the visitor to /auth from a route that requires auth;
            // a stale token must not kick anyone off a public page.
            const currentPath = window.location.pathname;
            if (currentPath.startsWith("/dashboard")) {
              navigateTo("/auth");
            }
          }, 0);
        }
      }

      // Format the error message for UI display
      let message = "Something went wrong";

      try {
        if (error.response?._data?.error?.messages?.length > 0) {
          message = error.response._data.error.messages[0];
        } else if (error.response?._data?.message) {
          message = Array.isArray(error.response._data.message)
            ? error.response._data.message[0]
            : error.response._data.message;
        }
      } catch (err) {
        console.error("Error parsing response data:", err);
      }

      error.response._data = {
        ...(error.response._data || {}),
        message,
      };
    },
  });

  return {
    provide: {
      customFetch: $customFetch,
    },
  };
});
